/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { Icon, Statistic, StatisticLabel, StatisticValue } from 'semantic-ui-react'
import { CardContent } from '../../../Shared/Card'
import React from 'react'
import useUser from '../../../../Shared/UserProvider/useUser'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale'
import { CardTitle } from '../../../Shared/CardTitle'
import { HUNDRED_THOUSAND, intlFormatMoneyWithCompact } from '../../../../../helpers/money'
import { intlFormatNumberWithCompact, TEN_THOUSAND } from '../../../../../helpers/number'
import { Link } from 'react-router-dom-v5-compat'
import { useTranslation } from 'react-i18next'
import { MissedRevenueBreakdownBar } from './MissedRevenueBreakdownBar'

const sectionTitleCss = css`
    justify-self: center;
    font-weight: 600;
    color: rgba(0, 0, 0, .87);
    > i {
        margin-left: .5rem;
    }
`

const MissedRevenueCardContent = ({
    from,
    to,
    totalMissedRevenue,
    revenuesMissed,
    missedOrder,
    noNegativeActivity,
    totalMissedRevenueUnavailable,
    totalMissedRevenueSubstituted,
    noUnavailableItems,
}) => {
    const [t] = useTranslation()
    const {user} = useUser()
    const currency = user.getCompanyCurrency()
    const locale = user.getCompanyLocale()

    const fmt = (amount) => intlFormatMoneyWithCompact(amount, currency, locale, HUNDRED_THOUSAND)
    const fmtN = (n) => intlFormatNumberWithCompact(n, locale, TEN_THOUSAND)

    return (
      <CardContent
        css={css`
            display: grid;
            place-items: center;
            gap: .35rem;
            grid-template-columns: 1fr;
            grid-template-rows: 30px auto auto 1fr 1fr;
            grid-template-areas:
                'title'
                'a'
                'breakdown'
                'block1'
                'block2';
        `}
      >
          <CardTitle title={format(new Date(), 'MMMM', {locale: fr})}/>

          <Statistic
            color="red"
            size="small"
            css={css`grid-area: a;

                &.ui.statistic, &.ui.statistic + .ui.statistic {
                    margin: 0;
                }`}
          >
              <StatisticValue>{fmt(totalMissedRevenue)}</StatisticValue>
              <StatisticLabel>{t('dashboard.missed_revenues.total_missed_revenues')}</StatisticLabel>
          </Statistic>

          <div css={css`grid-area: breakdown; width: 100%;`}>
              <MissedRevenueBreakdownBar
                  revenuesMissed={revenuesMissed}
                  totalMissedRevenueUnavailable={totalMissedRevenueUnavailable}
                  totalMissedRevenueSubstituted={totalMissedRevenueSubstituted}
                  formatFn={fmt}
              />
          </div>

          <div css={css`
              grid-area: block1;
              display: grid;
              place-items: center;
              gap: .5rem;
              width: 100%;
              grid-template-columns: 1fr 1fr;
              grid-template-rows: auto 1fr;
              grid-template-areas:
                  'title1 title1'
                  'c      b';
          `}>
              <Link
                to={{ pathname: '/analytics/missed', search: new URLSearchParams({ from, to }).toString() }}
                css={css`grid-area: title1; ${sectionTitleCss.styles}`}
              >
                  {t('dashboard.missed_revenues.not_acquired_orders', { count: missedOrder })}
                  <Icon name="arrow right"/>
              </Link>

              {noNegativeActivity ? (
                <div css={css`
                    grid-area: c / c / b / b;
                    display: flex;
                    align-items: center;
                    gap: .5rem;
                `}>
                    <Icon name="checkmark" color="green" />
                    <span css={css`font-weight: bold; font-size: .9rem;`}>{t('dashboard.missed_revenues.all_orders_acquired')}</span>
                </div>
              ) : (
                <>
                    <Statistic
                      color={missedOrder > 0 ? 'red' : undefined}
                      size="mini"
                      css={css`grid-area: b; &.ui.statistic, &.ui.statistic + .ui.statistic { margin: 0; }`}
                    >
                        <StatisticValue>{fmtN(missedOrder)}</StatisticValue>
                        <StatisticLabel>
                            {t('dashboard.missed_revenues.not_acquired_orders', { count: missedOrder })}
                        </StatisticLabel>
                    </Statistic>
                    <Statistic
                      color={revenuesMissed > 0 ? 'red' : undefined}
                      size="tiny"
                      css={css`grid-area: c; &.ui.statistic, &.ui.statistic + .ui.statistic { margin: 0; }`}
                    >
                        <StatisticValue>{fmt(revenuesMissed)}</StatisticValue>
                        <StatisticLabel>{t('dashboard.missed_revenues.missed_revenues')}</StatisticLabel>
                    </Statistic>
                </>
              )}
          </div>

          <div css={css`
              grid-area: block2;
              display: grid;
              place-items: center;
              gap: .5rem;
              width: 100%;
              grid-template-columns: 1fr 1fr;
              grid-template-rows: auto 1fr;
              grid-template-areas:
                  'title2 title2'
                  'e      d';
          `}>
              <Link
                to={{ pathname: '/analytics/unavailable-items', search: new URLSearchParams({ from, to }).toString() }}
                css={css`grid-area: title2; ${sectionTitleCss.styles}`}
              >
                  {t('unavailable_items.header')}
                  <Icon name="arrow right"/>
              </Link>

              {noUnavailableItems ? (
                <div css={css`
                    grid-area: e / e / d / d;
                    display: flex;
                    align-items: center;
                    gap: .5rem;
                `}>
                    <Icon name="checkmark" color="green" />
                    <span css={css`font-weight: bold; font-size: .9rem;`}>{t('unavailable_items.no_unavailable_product')}</span>
                </div>
              ) : (
                <>
                    <Statistic
                      color={totalMissedRevenueUnavailable > 0 ? 'red' : undefined}
                      size="tiny"
                      css={css`grid-area: e; &.ui.statistic, &.ui.statistic + .ui.statistic { margin: 0; }`}
                    >
                        <StatisticValue>{fmt(totalMissedRevenueUnavailable)}</StatisticValue>
                        <StatisticLabel>{t('dashboard.missed_revenues.removed_items')}</StatisticLabel>
                    </Statistic>
                    <Statistic
                      color={totalMissedRevenueSubstituted > 0 ? 'green' : undefined}
                      size="mini"
                      css={css`grid-area: d; &.ui.statistic, &.ui.statistic + .ui.statistic { margin: 0; }`}
                    >
                        <StatisticValue>{fmt(totalMissedRevenueSubstituted)}</StatisticValue>
                        <StatisticLabel>{t('dashboard.missed_revenues.removed_items_of_which_substituted')}</StatisticLabel>
                    </Statistic>
                </>
              )}
          </div>
      </CardContent>
    )
}

export { MissedRevenueCardContent }
